import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const PDF_MARGIN_MM = 14;
const PDF_FILENAME = 'PE-Profit-Rentabilitaetsvorschau.pdf';
/** Capture-Breite ≈ A4 bei 96dpi */
const CAPTURE_WIDTH_PX = 794;
const CAPTURE_SCALE = 1.5;

const waitForImages = (root: ParentNode): Promise<void> => {
    const images = Array.from(root.querySelectorAll('img'));
    if (images.length === 0) return Promise.resolve();

    return Promise.all(
        images.map(
            (img) =>
                new Promise<void>((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.addEventListener('load', () => resolve(), { once: true });
                    img.addEventListener('error', () => resolve(), { once: true });
                })
        )
    ).then(() => undefined);
};

type OffscreenMount = {
    host: HTMLElement;
    viewport: HTMLElement;
    clone: HTMLElement;
};

/**
 * Offscreen-Mount: Viewport clippt eine Seite, Clone wird per translateY verschoben.
 * Zuverlässiger als html2canvas-y-Crop (der oft den Rest abschneidet).
 */
const mountOffscreenClone = (documentEl: HTMLElement): OffscreenMount => {
    const host = document.createElement('div');
    host.setAttribute('data-pe-profit-pdf-host', 'true');
    host.style.cssText = [
        'position:fixed',
        'left:-12000px',
        'top:0',
        `width:${CAPTURE_WIDTH_PX}px`,
        'background:#ffffff',
        'pointer-events:none',
        'z-index:-1',
    ].join(';');

    const viewport = document.createElement('div');
    viewport.style.cssText = [
        'width:100%',
        'overflow:hidden',
        'background:#ffffff',
        'position:relative',
    ].join(';');

    const clone = documentEl.cloneNode(true) as HTMLElement;
    clone.classList.add('pe-profit-pdf-capture', 'pe-profit-export-mode');
    clone.style.cssText = [
        'width:100%',
        'max-width:none',
        'margin:0',
        'box-shadow:none',
        'border:none',
        'border-radius:0',
        'overflow:visible',
        'height:auto',
        'max-height:none',
        'position:relative',
        'will-change:transform',
    ].join(';');

    clone.querySelectorAll('.pe-profit-no-print').forEach((node) => node.remove());

    viewport.appendChild(clone);
    host.appendChild(viewport);
    document.body.appendChild(host);
    return { host, viewport, clone };
};

const captureViewportSlice = async (
    viewport: HTMLElement,
    clone: HTMLElement,
    sourceY: number,
    sliceHeight: number
): Promise<HTMLCanvasElement> => {
    const width = CAPTURE_WIDTH_PX;
    const height = Math.max(1, Math.ceil(sliceHeight));

    viewport.style.height = `${height}px`;
    clone.style.transform = `translateY(-${Math.max(0, Math.floor(sourceY))}px)`;

    // Reflow erzwingen
    void clone.offsetHeight;

    return html2canvas(viewport, {
        scale: CAPTURE_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0,
    });
};

/**
 * Gesamtes Dokument seitenweise → PDF.
 * Kein Section-Packing: der komplette Report inkl. ROI landet im Export.
 */
export const generatePeProfitPdfBlob = async (element: HTMLElement): Promise<Blob> => {
    const documentEl = element.querySelector('.pe-profit-document');
    if (!(documentEl instanceof HTMLElement)) {
        throw new Error('PE-Profit-Dokument nicht gefunden.');
    }

    const { host, viewport, clone } = mountOffscreenClone(documentEl);

    try {
        await waitForImages(clone);
        await new Promise((resolve) => window.setTimeout(resolve, 250));

        // Vollhöhe messen, bevor der Viewport geclippt wird
        viewport.style.height = 'auto';
        clone.style.transform = 'none';
        void clone.offsetHeight;
        const cssHeight = Math.max(clone.scrollHeight, clone.offsetHeight, 1);

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidthMm = pdf.internal.pageSize.getWidth();
        const pageHeightMm = pdf.internal.pageSize.getHeight();
        const printableWidthMm = pageWidthMm - PDF_MARGIN_MM * 2;
        const printableHeightMm = pageHeightMm - PDF_MARGIN_MM * 2;

        const pageCssHeight = (printableHeightMm / printableWidthMm) * CAPTURE_WIDTH_PX;

        let sourceY = 0;
        let pageIndex = 0;

        while (sourceY < cssHeight - 1) {
            if (pageIndex > 0) {
                pdf.addPage();
            }

            const sliceCssHeight = Math.min(pageCssHeight, cssHeight - sourceY);
            const canvas = await captureViewportSlice(viewport, clone, sourceY, sliceCssHeight);

            const imgHeightMm = Math.min(
                printableHeightMm,
                (canvas.height / Math.max(canvas.width, 1)) * printableWidthMm
            );

            pdf.addImage(
                canvas.toDataURL('image/jpeg', 0.93),
                'JPEG',
                PDF_MARGIN_MM,
                PDF_MARGIN_MM,
                printableWidthMm,
                imgHeightMm
            );

            sourceY += sliceCssHeight;
            pageIndex += 1;

            if (pageIndex > 40) {
                break;
            }
        }

        if (pageIndex === 0) {
            throw new Error('PDF konnte nicht erzeugt werden (leeres Dokument).');
        }

        return pdf.output('blob');
    } finally {
        host.remove();
    }
};

export const downloadPeProfitPdf = (blob: Blob, filename = PDF_FILENAME): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const printPeProfitPdfBlob = (blob: Blob): void => {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'PE Profit Druck');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const cleanup = () => {
        window.setTimeout(() => {
            iframe.remove();
            URL.revokeObjectURL(url);
        }, 1500);
    };

    iframe.onload = () => {
        try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } finally {
            cleanup();
        }
    };
    iframe.src = url;
};

export const PE_PROFIT_PDF_FILENAME = PDF_FILENAME;
